<template>
  <div class="login-wrap">
    <div class="login-card">
      <div class="login-logo">◈</div>
      <h1 class="login-title">HD Wallet</h1>
      <p class="login-sub">Ledger Multi-Chain Manager</p>

      <form class="login-form" @submit.prevent="doLogin">
        <div class="login-field">
          <label for="email">Email</label>
          <input
            type="email"
            id="email"
            v-model="email"
            class="form-control"
            placeholder="your@email.com"
            autocomplete="username"
            required
          />
        </div>

        <div class="login-field">
          <label for="password">Password</label>
          <div class="pw-wrap">
            <input
              :type="showPw ? 'text' : 'password'"
              id="password"
              v-model="password"
              class="form-control"
              placeholder="Password…"
              autocomplete="current-password"
              required
            />
            <button type="button" class="pw-eye" @click="showPw = !showPw" title="Toggle visibility">
              {{ showPw ? '🙈' : '👁️' }}
            </button>
          </div>
        </div>

        <button type="submit" class="btn-unlock" :disabled="loading">
          <span v-if="loading" class="spin">⟳</span>
          <span v-else>Unlock <span class="unlock-arrow">→</span></span>
        </button>

        <transition name="err-fade">
          <p v-if="error" class="login-error">⚠ {{ error }}</p>
        </transition>
      </form>

      <p class="login-footer">Secured · Multi-user HD Wallet Manager</p>
      <p class="build-stamp">v{{ version }} · built {{ buildTime }}</p>
    </div>
  </div>
</template>

<script>
import { loginUser } from '../services/pbService';
import { VERSION } from '../version';

export default {
  name: 'Login',
  data() {
    return {
      email: '', password: '', error: '', showPw: false, loading: false,
      version: VERSION,
      buildTime: process.env.VUE_APP_BUILD_TIME || new Date().toISOString().slice(0, 16).replace('T', ' '),
    };
  },
  methods: {
    async doLogin() {
      this.error = '';
      this.loading = true;
      try {
        await loginUser(this.email, this.password);
        this.$emit('login-success');
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Login failed';
        this.error = msg.includes('Failed to authenticate') ? 'Incorrect email or password.' : msg;
        this.password = '';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-wrap {
  min-height: calc(100vh - 54px);
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(ellipse 60% 40% at 50% -10%, rgba(99,102,241,0.14) 0%, transparent 70%),
    var(--bg, #0b0d14);
  padding: 32px 16px;
}

.login-card {
  width: 100%;
  max-width: 388px;
  background: var(--surface, #111520);
  border: 1px solid var(--border-2, #2e3650);
  border-radius: 22px;
  padding: 48px 40px 40px;
  text-align: center;
  box-shadow:
    0 24px 80px rgba(0,0,0,0.65),
    0 0 0 1px rgba(255,255,255,0.04) inset;
  animation: cardIn 0.45s cubic-bezier(0.16,1,0.3,1);
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(24px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.login-logo {
  font-size: 3.2rem;
  line-height: 1;
  background: linear-gradient(135deg, #6366f1, #a78bfa 60%, #c4b5fd);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 14px;
  filter: drop-shadow(0 0 24px rgba(99,102,241,0.55));
}

.login-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #e2e8f0;
  letter-spacing: -0.025em;
  margin: 0 0 4px;
}

.login-sub {
  font-size: 0.8rem;
  color: var(--text-muted, #52597a);
  margin: 0 0 34px;
  letter-spacing: 0.01em;
}

.login-form { text-align: left; }

.login-field { margin-bottom: 18px; }

.login-field label {
  display: block;
  font-size: 0.71rem;
  font-weight: 700;
  color: var(--text-muted, #52597a);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.pw-wrap { position: relative; }
.pw-wrap .form-control { padding-right: 46px !important; }

.pw-eye {
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
  line-height: 1;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.pw-eye:hover { opacity: 1; }

.btn-unlock {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 0.22s ease;
  box-shadow: 0 4px 20px rgba(99,102,241,0.38);
  margin-top: 6px;
}
.btn-unlock:hover:not(:disabled) {
  background: linear-gradient(135deg, #4f51cc, #7c3aed);
  box-shadow: 0 6px 30px rgba(99,102,241,0.52);
  transform: translateY(-1px);
}
.btn-unlock:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-unlock:active:not(:disabled) { transform: scale(0.98); }

.unlock-arrow {
  display: inline-block;
  margin-left: 6px;
  transition: transform 0.2s;
}
.btn-unlock:hover .unlock-arrow { transform: translateX(5px); }

.spin {
  display: inline-block;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.login-error {
  margin-top: 14px;
  color: #fb7185;
  font-size: 0.79rem;
  text-align: center;
  background: rgba(244,63,94,0.1);
  border: 1px solid rgba(244,63,94,0.22);
  border-radius: 8px;
  padding: 9px 14px;
}

.err-fade-enter-active,
.err-fade-leave-active { transition: opacity 0.2s, transform 0.2s; }
.err-fade-enter,
.err-fade-leave-to { opacity: 0; transform: translateY(-4px); }

.login-footer {
  margin: 22px 0 0;
  font-size: 0.7rem;
  color: var(--text-dim, #363d57);
  text-align: center;
  letter-spacing: 0.03em;
}

.build-stamp {
  margin: 6px 0 0;
  font-size: 0.65rem;
  color: var(--text-dim, #2a3050);
  text-align: center;
  font-family: monospace;
  letter-spacing: 0.04em;
}
</style>
